import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { environment } from '@env/environment';
import { MessageService } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { SoctripTranslationService } from 'src/app/services/soctrip-translation/soctrip-translation.service';
import { ToastType } from '@shared/enum/toast-type';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from 'src/app/services/storage/storage.service';
import { CommentPayload, CommentService } from 'src/app/services/comment/comment.service';
import { v5 as uuidv5 } from 'uuid';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';

@Component({
    selector: 'app-comments',
    templateUrl: './comments.component.html',
    styleUrls: ['./comments.component.scss']
})
export class CommentsComponent {
    @Input() id!: string;
    @ViewChildren('morePanel') morePanels!: QueryList<OverlayPanel>;
    @ViewChild('boxCreate') boxCreate!: ElementRef;
    @ViewChild('boxEdit') boxEdit!: ElementRef;

    apiUrl = environment.baseURL;
    idAdmin = this.getProfile('id');
    isServerImg: boolean = true;

    comments: any[] = [];
    commentId: any

    content = '';
    uploaded: { file: File; url: string }[] = [];

    pageNum  = 0;
    pageSize = 10;
    hasMore  = true;
    loadingMore = false;

    editingId: string | null = null;
    editContent = '';
    editFiles: { file: File; url: string }[] = [];
    editMediasBackup: any[] = [];

    loading = false;
    loadingEdit = false;
    loadingDelete = false;
    idDelete: any;
    activeRow = -1;
    activePreview = -1;
    isShowConfirmDeletePopup = false;
    isShowPopupPreviewMedia = false;
    expanded = new Set<string>();
    originalContent = '';
    originalMedias  : any[] = [];

    readonly accept = 'image/jpeg,image/jpg,image/png,video/mp4,video/avi,video/mov,video/wmv';
    avatarMyself = this.getProfile('avatar_thumbnail_url');

    constructor(
        private api: CommentService,
        private storage: StorageService,
        private toast: MessageService,
        private i18n: TranslateService,
        private soctripTranslationService: SoctripTranslationService,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        const NAMESPACE = uuidv5.DNS;
        this.commentId = uuidv5(`${this.id}-comment`, NAMESPACE);
        this.loadComments();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['id' ] && changes['id' ].currentValue) {
            const NAMESPACE = uuidv5.DNS;
            this.commentId = uuidv5(`${this.id}-comment`, NAMESPACE);
            this.pageNum  = 0;
            this.pageSize = 10;
            this.hasMore  = true;
            this.loadingMore = false;
            this.comments = [];
            this.loadComments();
        }
    }

    async create(): Promise<void> {
        if (!this.canCreate) return;
        this.loading = true;
        const tempId = 'tmp-' + Date.now();
        const tempMedia = this.uploaded.length ? {
                file: this.uploaded[0].file,
                url : this.uploaded[0].url,
                type: this.uploaded[0].file.type,
                isTemp: true,
            } : null;
        const newItem = {
            id: tempId,
            content: this.content,
            medias: tempMedia ? [tempMedia] : [],
            created_by: { 
                full_name: this.getProfile('full_name'),
                avatar_thumbnail_url: this.avatarMyself,
                id: this.getProfile('id') 
            }
        };
    
        queueMicrotask(() => (this.boxCreate.nativeElement.style.height = ''));
        const medias = await this.safeUpload(newItem.medias);

        if(medias === -1) {
            this.loading = false;
            return;
        }

        newItem.medias = medias;
        this.api.create(this.buildPayload(newItem.content, medias))
          .subscribe({
            next: (res: any) => {
                if(res.success) {
                    this.comments = [newItem, ...this.comments];
                    const idx = this.comments.findIndex(c => c.id === tempId);
                    if (idx !== -1) {
                        this.comments[idx].id = res.data.id;
                        this.comments[idx].created_at = res.data.created_at;
                    }
                }
                this.content = '';
                this.uploaded = [];
                this.loading = false;
            },
            error: () => {
                this.comments = this.comments.filter(c => c.id !== tempId);
                this.toast.add({
                    severity: ToastType.ERROR,
                    summary: 'Error',
                    detail: this.i18n.instant('COMMENTS.ADD_FAILED')
                });
                this.loading = false;
            }
          });
    }

    startEdit(c: any) {
        this.editingId = c.id;
        this.editContent = c.content;
        this.editMediasBackup = c.medias ?? [];
        this.editFiles = [];
        this.originalContent = c.content;
        this.originalMedias  = c.medias ?? [];
        this.cd.detectChanges();

        setTimeout(() => {
            const el = this.boxEdit?.nativeElement;
            if (el) {
                el.style.height = 'auto';
                el.style.height = el.scrollHeight + 'px';
                el.focus();
                el.selectionStart = el.selectionEnd = el.value.length;
            }
        });
    }

    formatCreatedAt(createdAt: string): string {
        const value = moment(createdAt);
        const now = moment();
        const diffInSeconds = now.diff(value, 'seconds');
        const language = (this.i18n.currentLang || 'en').toLowerCase();
    
        this.updateRelativeTimeLocale(language);
    
        if (diffInSeconds > 604800) {
            switch (language) {
                case 'vi':
                    return value.locale(language).format('DD [tháng] M [lúc] HH:mm');
                case 'en':
                    return value.locale(language).format('DD MMM [at] HH:mm');
                default:
                    return value.locale('en').format('DD MMM [at] HH:mm');
            }
        } else {
            return value.locale(language).fromNow();
        }
    }
    
    updateRelativeTimeLocale(language: string): void {
        const localeOverrides: Record<string, moment.RelativeTimeSpec> = {
            vi: {
                h: '1 giờ',
                hh: '%d giờ',
                m: '1 phút',
                mm: '%d phút',
                d: '1 ngày',
                dd: '%d ngày',
            },
            en: {
                h: '1 hour',
                hh: '%d hours',
                m: '1 minute',
                mm: '%d minutes',
                d: '1 day',
                dd: '%d days',
            }
        };
    
        if (localeOverrides[language]) {
            moment.updateLocale(language, {
                relativeTime: {
                    ...moment.localeData(language).relativeTime,
                    ...localeOverrides[language],
                }
            });
        }
    }

    scrollToComment(id: string | null): void {
        if (!id) return;
        const el = document.getElementById('comment-' + id);
        if (el) {
            el.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
            });
        }
    }

    async saveEdit(): Promise<void> {
        if (!this.editingId) return;
        this.loadingEdit = true;
        const idx = this.comments.findIndex(c => c.id === this.editingId);
        if (idx === -1) return;

        const backup = structuredClone(this.comments[idx]);

        if (this.editFiles.length) {
            this.comments[idx].medias = [ this.makeTempMedia(this.editFiles[0]) ];
        } else if (!this.editMediasBackup.length) {
            this.comments[idx].medias = [];
        } else {
            this.comments[idx].medias = [...this.editMediasBackup];
        }

        const currentId = this.editingId;
        this.scrollToComment(currentId);

        const mediasToUpload =
            this.editFiles.length ? await this.safeUpload(this.editFiles) : this.editMediasBackup;

        if(mediasToUpload === -1) {
            this.comments[idx] = backup;
            this.loadingEdit = false;
            return;
        }

        this.api
            .update(currentId, this.buildPayload(this.editContent, mediasToUpload))
            .subscribe({
            next: (res: any) => {
                if(res.success) {
                    this.comments[idx].content = this.editContent;
                    this.toast.add({
                        severity: ToastType.SUCCESS,
                        summary: 'Success',
                        detail: this.i18n.instant('COMMENTS.EDIT_SUCCESSFULL')
                    });
                    this.editingId = null;
                    this.activeRow = -1;
                }
                this.loadingEdit = false;
            },
            error: () => {
                this.comments[idx] = backup;
                this.toast.add({
                    severity: ToastType.ERROR,
                    summary: 'Error',
                    detail: this.i18n.instant('COMMENTS.EDIT_FAILED')
                });
                this.loadingEdit = false;
                this.editingId = null;
                this.activeRow = -1;
            },
        });
    }

    cancelEdit() {
        const targetId = this.editingId;
        this.editingId = null;
        this.editContent = '';
        this.editFiles = [];
        this.loading = false;
        this.activeRow = -1;
        this.cd.detectChanges();
        setTimeout(() => this.scrollToComment(targetId));
    }

    get canCreate(): boolean {
        return !!this.content.trim() || this.uploaded.length > 0;
    }

    get canSaveEdit(): boolean {
        const textChanged = this.editContent.trim() !== this.originalContent.trim();
        const addedNew    = this.editFiles.length > 0;
        const removedOld  = this.originalMedias.length > 0
                         && this.editFiles.length === 0
                         && this.editMediasBackup.length === 0;
      
        return textChanged || addedNew || removedOld;
    }

    delete(id: string) {
        const backupList = [...this.comments];
        this.comments = this.comments.filter(c => c.id !== id);
        this.afterSuccessDelete()

        this.api.remove(id).subscribe({
            next: () => {
                this.loadingDelete = false;
                this.toast.add({
                    severity: ToastType.SUCCESS,
                    summary: 'Success',
                    detail: this.i18n.instant('COMMENTS.DELETE_SUCCESSFULL'),
                });
            },
            error: () => {
                this.comments = backupList;
                this.loadingDelete = false;
                this.toast.add({
                    severity: ToastType.ERROR,
                    summary: 'Error',
                    detail: this.i18n.instant('COMMENTS.DELETE_FAILED'),
                });
            },
        });
    }

    onConfirmDeleteComment() {
        this.delete(this.idDelete);
    }

    isExpanded(id: string): boolean {
        return this.expanded.has(id);
    }

    toggleExpand(id: string): void {
        this.isExpanded(id) ? this.expanded.delete(id) : this.expanded.add(id);
    }

    shouldShowToggle(text: string) {
        const lineBreaks = (text.match(/\n/g) || []).length;
        return text.length > 250 || lineBreaks >= 3;    
    }

    getProfile(key: string) {
        return this.soctripTranslationService.getKeyLocalUserProfile(key);
    }

    onCloseConfirmDelete() {
        this.isShowConfirmDeletePopup = false;
    }

    onShowConfirmDelete(id: string) {
        this.idDelete = id
        this.isShowConfirmDeletePopup = true;
    }

    async onSelect(e: Event, target: 'create' | 'edit'): Promise<void> {
        const files = (e.target as HTMLInputElement).files;
        if (!files?.length) return;

        const holder = target === 'create' ? this.uploaded : this.editFiles;
        const ok = await this.validate(files[0]);

        if (ok) holder[0] = { file: files[0], url: URL.createObjectURL(files[0]) };
        (e.target as HTMLInputElement).value = '';

        if (target === 'create') {
            setTimeout(() => {
                const el = this.boxCreate?.nativeElement;
                if (el) {
                    el.focus();
                    el.selectionStart = el.selectionEnd = el.value.length;
                }
            });
        } else {
            setTimeout(() => {
                const el = this.boxEdit?.nativeElement;
                if (el) {
                    el.focus();
                    el.selectionStart = el.selectionEnd = el.value.length;
                }
            });
        }
    }

    replaceWithDefaultImage(event: any): void {
        event.target.src = 'assets/images/user-management/default-image.jpg';
    }

    replaceWithDefaultAvatar(event: any): void {
        event.target.src = 'assets/images/default_user_avatar.png';
    }

    removeFile(target: 'create' | 'edit'): void {
        target === 'create' ? (this.uploaded = []) : (this.editFiles = []);
    }

    toggleMore(ev: MouseEvent, idx: number, panel: OverlayPanel): void {
        this.activeRow = idx;
        panel.toggle(ev);
    }

    getImageUrl(id: string | undefined): string {
        if (!id) {
            return '';
        }
        const isPath = id.startsWith('/');
        const webPath = isPath ? '' : '/storage/files/web/';
        const addWebp = isPath ? '' : '.webp';
        return `${this.apiUrl}${webPath}${id}${addWebp}`;
    }

    isImg(x: any): boolean {
        return (x.type || x.file?.type).includes('image/');
    }

    isVid(x: any): boolean {
        return (x.type || x.file?.type).includes('video/');
    }

    onScroll(e: Event): void {
        const el = e.target as HTMLElement;
        const threshold = 100;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      
        if (atBottom && this.hasMore && !this.loadingMore) {
          this.loadComments();
        }
    }

    loadComments(reset = false) {
        if (this.loadingMore || (!this.hasMore && !reset)) return;
      
        if (reset) {
          this.pageNum = 0;
          this.hasMore = true;
          this.comments = [];
        }
      
        this.loadingMore = true;
        this.api.list(this.commentId, this.pageNum, this.pageSize)
            .subscribe(res => {
                const data = res?.data ?? [];
                this.comments = reset ? data : [...this.comments, ...data];
                this.hasMore = data.length === this.pageSize;
                if (this.hasMore) this.pageNum++;
                this.loadingMore = false;
            });
    }

    handleKeydownCreate(e: KeyboardEvent) {
        if (e.key !== 'Enter') return;
        if (e.shiftKey || e.ctrlKey) return;

        e.preventDefault();
        if (this.canCreate) { 
            this.create();
        }
    }

    getMediaSrc(m: any): string {
        if (m.url) return m.url;
        if (this.isImg(m)) {
            return `${this.apiUrl}/storage/files/web/${m.id}.webp`;
        }
        return `${this.apiUrl}/storage/files/stream-video/${m.id}${m.extension}`;
      }

    handleKeydownEdit(e: KeyboardEvent) {
        if (e.key !== 'Enter') return;
        if (e.shiftKey || e.ctrlKey) return;
      
        e.preventDefault();
        if (this.canSaveEdit) this.saveEdit();
    }

    afterSuccessCreate() {
        this.content = '';
        this.uploaded = [];
        this.loading = false;
        queueMicrotask(() => {
            const el = this.boxCreate.nativeElement;
            el.style.height = '';
        });
        this.loadComments(true);
    }

    afterSuccessEdit() {
        this.cancelEdit();
        this.loadComments(true);
        queueMicrotask(() => {
            if(this.boxEdit) {
                const el = this.boxEdit.nativeElement;
                el.style.height = '';
            }
        });
    }

    afterSuccessDelete() {
        this.loadingDelete = false;
        this.isShowConfirmDeletePopup = false;
    }

    buildPayload(text: string, medias: any[]): CommentPayload {
        return {
            object_id: this.commentId,
            parent_id: this.commentId,
            object_type: 'POST',
            content: text,
            message_ranges: [],
            medias,
            language: 'vi',
            preview_data: null,
            owner_type: 'USER',
        };
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent) {
        if (this.hasUnsavedComment) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    get hasUnsavedComment(): boolean {
        return !!this.content.trim() || this.uploaded.length > 0 ||
               this.editingId !== null;
    }

    async safeUpload(list: { file: File }[]): Promise<any> {
        if (!list.length) return [];
        try {
          const fd = new FormData();
          fd.append('files', list[0].file);
      
          const rsp = await firstValueFrom(this.storage.uploadFiles(fd));
          if (rsp?.success && rsp.data?.length) {
            return rsp.data.map((d: any) => ({
              id: d.id,
              extension: d.extension,
              original_name: d.original_name,
              type: d.type,
              signature: d.signature,
            }));
          }
        } catch (err) {
            this.toast.add({
                severity: ToastType.WARNING,
                summary : 'Warning',
                detail  : this.i18n.instant('COMMENTS.UPLOAD_FAILED_SKIP_MEDIA'),
            });
            return -1;
        }
      }

    prviewMedia(index: number, comment: any) {
        if(comment.medias[0]?.isTemp) {
            this.isServerImg = false;
        } else this.isServerImg = true;
        this.activePreview = index;
        this.isShowPopupPreviewMedia = true;

    }

    onClosePopupPreview() {
        this.isServerImg = false;
        this.isShowPopupPreviewMedia = false;
    }

    makeTempMedia(uploadedItem: { file: File; url: string }) {
        return {
            file: uploadedItem.file,
            url : uploadedItem.url,
            type: uploadedItem.file.type,
            isTemp: true,
        };
    }

    async validate(file: File): Promise<boolean> {
        const types = this.accept.split(',');
        const sizeKB = file.size / 1024;

        let msg = '';
        if (!types.includes(file.type)) msg = 'COMMON.ONLY_ACCEPT_FILE_TYPE';
        else if (sizeKB < 10) msg = 'COMMON.FILE_IS_TOO_SMALL';
        else if (sizeKB > 10240) msg = 'COMMON.VIDEO_FILE_IS_TOO_LARGE';

        if (msg) {
            this.toast.add({
                severity: ToastType.ERROR,
                summary: 'Error',
                detail: this.i18n.instant(msg),
            });
            return false;
        }
        return true;
    }
}
